import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'

export interface AssessmentCardProps {
  sectionsCompleted?: number
  totalSections?: number
  progressPercentage?: number
  status?: string
  completedAt?: string | undefined
  attemptNumber?: number | undefined
  totalAttempts?: number | undefined
  privacyNote?: string
  onContinue?: () => void
  onRetake?: () => void
  onViewResults?: () => void
  className?: string
}

export function AssessmentCard({
  sectionsCompleted = 0,
  totalSections = 0,
  progressPercentage = 0,
  status,
  completedAt,
  attemptNumber,
  totalAttempts = 0,
  privacyNote = 'Take your time — your answers are private and only visible to your counsellor.',
  onContinue,
  onRetake,
  onViewResults,
  className,
}: AssessmentCardProps) {
  const isCompleted = status === 'completed'
  const isNotStarted = status === 'not_started' || (!status && sectionsCompleted === 0)
  const displayProgress = isCompleted ? 100 : Math.min(progressPercentage, 100)

  return (
    <div
      className={cn('rounded-[20px] bg-[#F3F4F6] px-4 sm:px-[25px] py-8 sm:py-[59px] w-full min-h-[280px] sm:min-h-[329px] flex flex-col justify-center', className)}
    >
      <div className='flex flex-col gap-[28px] w-full'>
        {/* Header */}
        <div className='flex flex-col gap-[16px] w-full'>
          <div className='flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3'>
            <div className='flex flex-col gap-[10px]'>
              <p className='text-[18px] text-[#4e4d4d] font-normal leading-[28px]'>Recently Activity</p>
              <h2 className='text-[32px] font-bold text-black leading-[40px] tracking-[-0.5px]'>
                Your Assessment
              </h2>
            </div>
            {!isNotStarted && (
              <div className='flex items-center gap-2'>
                {attemptNumber && totalAttempts > 0 && (
                  <div className='rounded-full border border-[#4e4d4d] px-3 py-2 h-[40px] flex items-center justify-center w-fit'>
                    <span className='text-[12px] font-bold text-[#4e4d4d]'>
                      Attempt #{attemptNumber}
                    </span>
                  </div>
                )}
                <div className='rounded-full border border-[#151515] px-4 py-2 h-[40px] flex items-center justify-center w-fit'>
                  <span className='text-[12px] font-bold text-[#151515]'>
                    {sectionsCompleted}/{totalSections} sections done
                  </span>
                </div>
              </div>
            )}
          </div>

          {/* Progress Bar */}
          {!isNotStarted && (
            <div className='flex flex-col gap-[8px] w-full'>
              <p className='text-[12px] text-black leading-[16px]'>
                {isCompleted ? (
                  <>
                    <span className='font-bold'>Assessment completed!</span> You finished on{' '}
                    {completedAt ? new Date(completedAt).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    }) : 'recently'}
                  </>
                ) : (
                  <>
                    <span className='font-bold'>{displayProgress}% </span>
                    <span className='font-normal'>of your assessment is done - great progress!</span>
                  </>
                )}
              </p>
              <div className='h-[4px] w-full rounded-full bg-[#4e4d4d]/25 relative'>
                <div
                  className={`absolute inset-y-0 left-0 rounded-full transition-all duration-300 ${isCompleted ? 'bg-green-500' : 'bg-[#FF6600]'}`}
                  style={{ width: `${displayProgress}%` }}
                  role='progressbar'
                  aria-valuenow={displayProgress}
                  aria-valuemin={0}
                  aria-valuemax={100}
                  aria-label={`Assessment progress: ${displayProgress}%`}
                />
              </div>
            </div>
          )}
        </div>

        {/* Action Buttons */}
        {isNotStarted ? (
          <Button
            onClick={onContinue}
            className='h-[51px] w-full sm:w-[257px] rounded-[8px] bg-[#FF6600] text-[16px] font-bold text-white hover:bg-[#e55109]'
          >
            Start Assessment
          </Button>
        ) : isCompleted ? (
          <div className='flex items-center gap-3 flex-wrap'>
            <Button
              onClick={onViewResults}
              className='h-[51px] rounded-[8px] bg-green-600 text-[16px] font-bold text-white hover:bg-green-700 px-6'
            >
              View Full Results
            </Button>
            <Button
              onClick={onRetake}
              className='h-[51px] rounded-[8px] bg-[#151515] text-[16px] font-bold text-white hover:bg-[#2a2a2a] px-6'
            >
              Retake Assessment
            </Button>
          </div>
        ) : (
          <Button
            onClick={onContinue}
            className='h-[51px] w-full sm:w-[257px] rounded-[8px] bg-[#151515] text-[16px] font-bold text-white hover:bg-[#2a2a2a]'
          >
            Continue Assessment
          </Button>
        )}
      </div>

      {/* Privacy Note */}
      {!isCompleted && (
        <p className='text-[14px] text-[#4e4d4d] font-normal leading-[24px] mt-[44px]'>
          {privacyNote}
        </p>
      )}
    </div>
  )
}
